/** Shared utilities for any calendar grid view. Both the main Calendar page
 *  and the per-person preview in Settings render the same look, so the date
 *  helpers + leave-type colour mapping live here.
 */
import type { AbsenceView } from "../api/types";

export type YearMonth = { year: number; month: number /* 0-11 */ };

export function todayYM(): YearMonth {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() };
}

export function shiftMonth(ym: YearMonth, delta: number): YearMonth {
  const d = new Date(ym.year, ym.month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

export function daysInMonth(ym: YearMonth): Date[] {
  const last = new Date(ym.year, ym.month + 1, 0).getDate();
  return Array.from({ length: last }, (_, i) => new Date(ym.year, ym.month, i + 1));
}

export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function isWeekend(d: Date): boolean {
  const wd = d.getDay();
  return wd === 0 || wd === 6;
}

/** Map a Boond leave-type label to a brand-palette CSS variable.
 *  Order matters — first match wins. Keep the more-specific checks (family,
 *  unpaid) before the generic "cp" / "payé" catch which would otherwise
 *  swallow "congé maternité payé" etc. */
export function colourForType(type: string | null): string {
  if (!type) return "var(--leave-binary)";
  const t = type.toLowerCase();
  if (t.includes("maladie") || t.includes("sick") || t.includes("arrêt") || t.includes("arret")) return "var(--leave-maladie)";
  if (
    t.includes("paternit") ||
    t.includes("maternit") ||
    t.includes("naissance") ||
    t.includes("parental") ||
    t.includes("enfant malade") ||
    t.includes("garde")
  ) return "var(--leave-family)";
  if (t.includes("sans solde") || t.includes("unpaid")) return "var(--leave-unpaid)";
  if (
    t.includes("exceptionnel") ||
    t.includes("événement") ||
    t.includes("evenement") ||
    t.includes("mariage") ||
    t.includes("deuil")
  ) return "var(--leave-special)";
  if (t.includes("formation")) return "var(--leave-formation)";
  if (t.includes("rtt")) return "var(--leave-rtt)";
  if (t.includes("cp") || t.includes("payé") || t.includes("paye") || t.includes("vacation")) return "var(--leave-cp)";
  return "var(--leave-other)";
}

export function findAbsence(absences: AbsenceView[], dayIso: string): AbsenceView | undefined {
  return absences.find((a) => a.start_date <= dayIso && dayIso <= a.end_date);
}

/** Every absence covering `dayIso`, not just the first one. */
export function findAbsences(absences: AbsenceView[], dayIso: string): AbsenceView[] {
  return absences.filter((a) => a.start_date <= dayIso && dayIso <= a.end_date);
}

/** How much of a day a person is away, and under which leave types. */
export interface DayCoverage {
  /** The absences covering the day, in the order the server sent them. */
  absences: AbsenceView[];
  /** true when the whole working day is covered. */
  full: boolean;
  /** Distinct leave types involved, order preserved. null = binary visibility. */
  types: (string | null)[];
}

/**
 * Work out whether a day is fully or half covered.
 *
 * Boond lets someone book two half-days on the same date — typically a morning
 * of CP and an afternoon of RTT. Eleven people had done it when this was
 * written (12 dates in production). The previous code called `findAbsence`,
 * which returns only the FIRST match, saw `is_half_day` on it and painted half
 * the cell: the calendar showed a half day off when the person was away all
 * day. Two half-days cover the day, so `full` is true.
 *
 * Boond does not say WHICH half each booking is (hence the single
 * `is_half_day` flag), so we deliberately do not claim one is the morning —
 * the cell is painted as one full day and the tooltip lists every type.
 */
export function dayCoverage(absences: AbsenceView[], dayIso: string): DayCoverage | null {
  const covering = findAbsences(absences, dayIso);
  if (covering.length === 0) return null;
  const halves = covering.filter((a) => a.is_half_day).length;
  // A full-day booking, or two (or more) half-days, covers the whole day.
  const full = covering.some((a) => !a.is_half_day) || halves >= 2;
  const types: (string | null)[] = [];
  for (const a of covering) {
    if (!types.includes(a.leave_type)) types.push(a.leave_type);
  }
  return { absences: covering, full, types };
}

/** One person away on a given day, as shown in the date-header tooltip. */
export interface DayAway {
  name: string;
  /** null when the viewer only has binary visibility on this person. */
  type: string | null;
  half: boolean;
}

/** Already-translated labels, so this stays a pure function (no i18n import). */
export interface DayTooltipLabels {
  /** e.g. "Absents (3) :" */
  awayOnDay: (count: number) => string;
  /** e.g. "Aucun absent" */
  noneAway: string;
  /** Leave-type fallback in binary mode, e.g. "Absent". */
  binary: string;
  /** e.g. "demi-journée" */
  halfDay: string;
  /** e.g. "… et 4 autre(s)" */
  andNMore: (count: number) => string;
}

/**
 * A native `title` tooltip has no scrollbar — an August column with 40 people
 * away would run off the screen. Cap the list and summarise the overflow.
 */
export const TOOLTIP_MAX_NAMES = 12;

/**
 * Text of the calendar date-header tooltip: the date line, then who is away
 * that day. `head` is the already-formatted date (plus holiday, if any).
 */
export function buildDayTooltip(
  head: string,
  away: DayAway[],
  labels: DayTooltipLabels,
  max: number = TOOLTIP_MAX_NAMES,
): string {
  if (away.length === 0) return `${head}\n${labels.noneAway}`;
  const shown = away.slice(0, max);
  const lines = shown.map(
    (p) => `• ${p.name} — ${p.type ?? labels.binary}${p.half ? ` (${labels.halfDay})` : ""}`,
  );
  if (away.length > shown.length) {
    lines.push(labels.andNMore(away.length - shown.length));
  }
  return [head, labels.awayOnDay(away.length), ...lines].join("\n");
}

export const WEEKDAY_FR = ["D", "L", "M", "M", "J", "V", "S"];
export const WEEKDAY_EN = ["S", "M", "T", "W", "T", "F", "S"];
